// src/pages/CartPage.js
import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import CartItem from "../components/CartItem";
import { useCart } from "../context/CartContext";
import { apiRequest } from "../services/api";

function CartPage() {
  const { lang } = useContext(LanguageContext);
  const navigate = useNavigate();
  const { cartItems, subtotal } = useCart();

  const isEmpty = cartItems.length === 0;

  const [shipping, setShipping] = useState({
    fee: 4,
    free_enabled: false,
    free_threshold: 0,
  });

  const hasOutOfStock = cartItems.some((item) => item.stock_qty === 0);
  

  const disableCheckout = hasOutOfStock;

  const TEXT = {
    EN: {
      title: "YOUR CART",
      empty: "Your cart is empty.",
      continue: "Continue Shopping",
      wishlist: "View Wishlist",
      subtotal: "Subtotal",
      total: "Total",
      checkout: "PROCEED TO CHECKOUT",
      deliveryInfo: "Delivery fees",
      freeDeliveryInfo: "Free delivery on orders above",
      pickupInfo: "Pick up from store",
    },
    AR: {
      title: "سلة المشتريات",
      empty: "سلة المشتريات فارغة.",
      continue: "متابعة التسوق",
      wishlist: "عرض المفضلة",
      subtotal: "المجموع",
      total: "الإجمالي",
      checkout: "إتمام الشراء",
      deliveryInfo: "رسوم التوصيل",
      freeDeliveryInfo: "توصيل مجاني للطلبات فوق",
      pickupInfo: "استلام من المحل",
    },
  };

  const t = TEXT[lang];

  useEffect(() => {
    apiRequest("/shipping-settings")
      .then((s) =>
        setShipping({
          fee: Number(s.default_fee ?? 4),
          free_enabled: !!s.free_enabled,
          free_threshold: Number(s.free_threshold ?? 0),
        })
      )
      .catch(() => {});
  }, []);

  const finalTotal = Number(subtotal) || 0;

  return (
    <div
      className="w-full min-h-screen bg-[#0F1A12] pt-[95px] px-4 pb-24 text-white"
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      {/* BACK */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-[80px] left-4 text-3xl opacity-70 hover:opacity-100"
      >
        ×
      </button>

      {/* TITLE */}
      <h1 className="text-xl font-bold text-center tracking-widest">
        {t.title}
      </h1>
      <div className="h-[2px] w-16 bg-[#2E5A2A] mx-auto mt-2 mb-6" />

      {/* EMPTY */}
      {isEmpty && (
        <div className="flex flex-col items-center justify-center mt-24">
          <div className="w-20 h-20 border border-[#2E5A2A] rounded-full flex items-center justify-center">
            <img src="/icons/bag.svg" className="w-9 opacity-60" alt="" />
          </div>

          <p className="text-[#9FB39C] text-sm mt-4">{t.empty}</p>

          <button
            onClick={() => navigate("/")}
            className="mt-6 px-6 py-3 bg-[#2E5A2A] rounded-md font-semibold text-sm hover:brightness-110"
          >
            {t.continue}
          </button>

          <button
            onClick={() => navigate("/wishlist")}
            className="mt-3 px-6 py-3 border border-[#2E5A2A] rounded-md text-sm font-semibold hover:bg-[#2E5A2A]/20"
          >
            {t.wishlist}
          </button>
        </div>
      )}

      {/* ITEMS */}
      {!isEmpty && (
        <div className="flex flex-col gap-4">
          {cartItems.map((item) => (
            <CartItem key={item.id} item={item} lang={lang} />
          ))}
        </div>
      )}

      {/* WARNINGS */}
      {hasOutOfStock && (
        <div className="mt-4 p-3 border border-red-700 bg-red-900/30 text-red-400 text-sm text-center">
          {lang === "EN"
            ? "Some items are out of stock. Remove them to continue."
            : "بعض المنتجات نفدت من المخزون. يرجى حذفها للمتابعة."}
        </div>
      )}


      {/* SUMMARY */}
      {!isEmpty && (
        <div className="mt-8 border border-[#2E5A2A] rounded-lg p-5 bg-[#121F17]">
          <div className="flex justify-between text-sm mb-3">
            <span className="text-[#9FB39C]">{t.subtotal}</span>
            <span className="font-semibold">${finalTotal.toFixed(2)}</span>
          </div>

          <div className="text-xs text-[#9FB39C] space-y-1 mb-3">
            <p>• {t.deliveryInfo}: ${shipping.fee.toFixed(2)}</p>
            {shipping.free_enabled && shipping.free_threshold > 0 && (
              <p>
                • {t.freeDeliveryInfo}: $
                {shipping.free_threshold.toFixed(2)}
              </p>
            )}
            <p>• {t.pickupInfo}: $0.00</p>
          </div>

          <hr className="my-4 border-[#2E5A2A]" />

          <div className="flex justify-between text-lg font-bold">
            <span>{t.total}</span>
            <span>${finalTotal.toFixed(2)}</span>
          </div>

          {/* CHECKOUT */}
          <button
            disabled={disableCheckout}
            onClick={() => {
              if (disableCheckout) return;

              apiRequest("/analytics/track", {
                method: "POST",
                body: JSON.stringify({
                  action_type: "enter_checkout",
                  entity_type: "cart",
                }),
              }).catch(() => {});

              if (typeof window.fbq === "function") {
  window.fbq("track", "InitiateCheckout", {
    content_ids: cartItems.map(
      (it) => it.public_id || it.product_id
    ),
    content_type: "product",
    value: finalTotal,
    currency: "USD",
    num_items: cartItems.reduce(
      (s, it) => s + Number(it.qty || 0),
      0
    ),
  });
}


              navigate("/checkout");
            }}
            className={`w-full mt-5 py-3 rounded-md text-lg font-semibold transition
              ${
                disableCheckout
                  ? "bg-gray-600 cursor-not-allowed"
                  : "bg-[#2E5A2A] hover:brightness-110 active:scale-95"
              }
            `}
          >
            {t.checkout}
          </button>

          <button
            onClick={() => navigate("/wishlist")}
            className="w-full mt-3 py-3 border border-[#2E5A2A] rounded-md font-semibold text-sm hover:bg-[#2E5A2A]/20"
          >
            {t.wishlist}
          </button>
        </div>
      )}
    </div>
  );
}

export default CartPage;
